import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Container,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Search as SearchIcon } from '@mui/icons-material';
import api from '../../../services/api';
import Paginate from '../../../components/Paginate';

const itemsPerPage = 5;

const ViewResponsesForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [formTemplate, setFormTemplate] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeUser, setTypeUser] = useState(null);

  useEffect(() => {
    setTypeUser(location.pathname.split('/')[1]);
  }, [location.pathname]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const templateResponse = await api.get(`/form-templates/${id}`);
        setFormTemplate(templateResponse.data);
        setResponses(templateResponse.data.responses || []);
        setError(null);
      } catch (err) {
        setError('Erro ao carregar os dados do formulário ou respostas');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const fieldMap = useMemo(() => {
    const map = new Map();
    formTemplate?.sections?.forEach((section) =>
      section.fields.forEach((field) => map.set(field.id, field))
    );
    return map;
  }, [formTemplate]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleViewResponse = (responseId) => {
    const response = responses.find((resp) => resp.id === responseId);
    if (response && typeUser) {
      navigate(`/${typeUser}/form-responses/response/${response.answeredBy}/${id}`);
    }
  };

  const filteredResponses = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();
    return responses.filter((response) =>
      response.answerer.name.toLowerCase().includes(lowerSearch)
    );
  }, [responses, searchTerm]);

  const visibleResponses = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredResponses.slice(startIndex, endIndex);
  }, [filteredResponses, page]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={isMobile ? 30 : 40} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4, maxWidth: 'md', mx: 'auto' }}>
        <Alert severity="error">{error}</Alert>
        <Button
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIcon />}
          sx={{
            mt: 2,
            fontSize: isMobile ? '0.8rem' : '0.875rem',
            borderColor: '#10641c',
            color: '#10641c',
            '&:hover': {
              borderColor: '#0a3d0f',
              color: '#0a3d0f',
              backgroundColor: '#e8f5e9',
            },
          }}
          variant="outlined"
        >
          Voltar
        </Button>
      </Container>
    );
  }

  if (!formTemplate) {
    return (
      <Container sx={{ mt: 4, maxWidth: 'md', mx: 'auto' }}>
        <Alert severity="warning">Formulário não encontrado.</Alert>
        <Button
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIcon />}
          sx={{
            mt: 2,
            fontSize: isMobile ? '0.8rem' : '0.875rem',
            borderColor: '#10641c',
            color: '#10641c',
            '&:hover': {
              borderColor: '#0a3d0f',
              color: '#0a3d0f',
              backgroundColor: '#e8f5e9',
            },
          }}
          variant="outlined"
        >
          Voltar
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth={isMobile ? 'xs' : 'md'} sx={{ mt: isMobile ? 2 : 0, mb: 4, mx: 'auto' }}>
      <Typography
        variant={isMobile ? 'h6' : 'h5'}
        align="center"
        sx={{ color: '#10641c', fontWeight: 'bold', mb: 3 }}
      >
        Respostas do Formulário
      </Typography>

      <Paper
        sx={{
          p: isMobile ? 2 : 3,
          mb: 3,
          borderRadius: 2,
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
          borderLeft: '4px solid #10641c',
          bgcolor: '#fff',
        }}
      >
        <Typography
          variant={isMobile ? 'h6' : 'h5'}
          gutterBottom
          sx={{ color: '#10641c', fontWeight: 'bold' }}
        >
          {formTemplate.name}
        </Typography>
        {formTemplate.description && (
          <Typography variant="body1" color="text.secondary">
            {formTemplate.description}
          </Typography>
        )}
      </Paper>

      <Paper
        sx={{
          p: isMobile ? 2 : 3,
          borderRadius: 2,
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
          borderLeft: '4px solid #10641c',
          bgcolor: '#fff',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}> {/* Alterado para column */}
          <Typography variant="h6" sx={{ color: '#10641c', fontWeight: 'bold' }}>
            Respostas
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', mt: 0.5 }}>
            Total: {<strong>{filteredResponses.length}</strong>}
          </Typography>
        </Box>
        <Box sx={{ width: '100%', mb: isMobile ? 2 : 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Buscar por nome..."
            value={searchTerm}
            onChange={handleSearch}
            sx={{
              height: '45px',
              '& .MuiOutlinedInput-root': {
                borderRadius: isMobile ? '6px' : '8px',
                fontSize: isMobile ? '1rem' : '1rem',
                height: 'inherit',
                padding: '0 14px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ced4da',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#10641c',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#10641c',
                },
                '& .MuiInputBase-input': {
                  fontSize: isMobile ? '1rem' : '1rem',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize={isMobile ? 'medium' : 'medium'} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <TableContainer component={Paper} sx={{ maxWidth: '100%' }}>
            <Table
              sx={{
                minWidth: isMobile ? 'auto' : 650,
                '& .MuiTableCell-head': { fontWeight: 'bold' },
                '& .MuiTableCell-root': {
                  fontSize: isMobile ? '0.8rem' : '0.875rem',
                  p: isMobile ? 1 : 2,
                },
              }}
              aria-label="responses table"
            >
              <TableHead sx={{ bgcolor: '#f8f8f8' }}>
                <TableRow>
                  <TableCell align="center">Empreendedor</TableCell>
                  <TableCell align="center">Data/Hora</TableCell>
                  <TableCell align="center">Respostas</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleResponses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem', color: '#666' }}>
                        Nenhuma resposta encontrada.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleResponses.map((response) => (
                    <TableRow
                      key={response.id}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                      }}
                    >
                      <TableCell align="center">{response.answerer.name}</TableCell>
                      <TableCell align="center">
                        {new Date(response.dateAnswered).toLocaleString('pt-BR')}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="text"
                          onClick={() => handleViewResponse(response.id)}
                          sx={{ color: '#10641c', textTransform: 'none' }}
                        >
                          Visualizar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      {filteredResponses.length > itemsPerPage && (
        <Box sx={{ mt: isMobile ? 1 : 3, display: 'flex', justifyContent: 'center' }}>
          <Paginate
            count={Math.ceil(filteredResponses.length / itemsPerPage)}
            page={page}
            onChange={handlePageChange}
            size={isMobile ? 'small' : 'medium'}
          />
        </Box>
      )}

      {/*
      <Box sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            fontSize: isMobile ? '0.8rem' : '0.875rem',
            borderColor: '#10641c',
            color: '#10641c',
            padding: isMobile ? '6px 12px' : '8px 16px',
            borderRadius: '8px',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              borderColor: '#0a3d0f',
              color: '#0a3d0f',
              backgroundColor: '#e8f5e9',
              transform: 'scale(1.05)',
            },
          }}
        >
          Voltar
        </Button>
      </Box>
*/}
    </Container>
  );
};

export default ViewResponsesForm;